// src/utils/aiResponses.ts - Updated to use Supabase Edge Functions
import { supabase } from '../lib/supabase';

export interface ChatSource {
  id: string;
  title: string;
  slug: string | null;
  content_type: string;
}

interface AIResponse {
  message: string;
  context?: unknown[];
  sources?: ChatSource[];
  intent?: 'hiring' | 'partnership' | null;
  isNetworkError?: boolean;
}

function getOrCreateSessionId(): string {
  const stored = localStorage.getItem('joeai_session_id');
  if (stored) return stored;
  const fresh = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem('joeai_session_id', fresh);
  } catch {
    // localStorage unavailable — fall through with ephemeral id
  }
  return fresh;
}

// Main function to generate AI response using Edge Function
export async function generateAIResponse(userMessage: string, conversationHistory: any[] = []): Promise<AIResponse> {
  try {
    console.log('🤖 Calling Supabase Edge Function for:', userMessage);

    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: {
        message: userMessage,
        sessionId: getOrCreateSessionId(),
        conversationHistory: conversationHistory.slice(-5).map(msg => ({
          role: msg.isUser ? 'user' : 'assistant',
          content: msg.content
        }))
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      throw error;
    }

    console.log('✅ Got response from edge function');

    const sources: ChatSource[] = Array.isArray(data?.sources)
      ? (data.sources as unknown[]).filter(
          (s): s is ChatSource =>
            typeof s === 'object' &&
            s !== null &&
            typeof (s as ChatSource).id === 'string' &&
            typeof (s as ChatSource).title === 'string'
        )
      : [];

    return {
      message: data.response,
      context: data.context || [],
      sources,
      intent: data.intent ?? null,
    };

  } catch (error: any) {
    console.error('❌ AI response generation failed:', error);

    // Detect whether this is a network-level failure (firewall, DNS, timeout)
    // vs an API-level error (rate limit, server error, etc.)
    const errorMessage = (error?.message || '').toLowerCase();
    const networkSignals = [
      'failed to fetch', 'networkerror', 'network request failed',
      'load failed', 'aborted', 'err_blocked', 'err_connection',
      'err_cert', 'err_ssl', 'err_tunnel', 'timeout', 'dns'
    ];
    const isNetworkError = networkSignals.some(s => errorMessage.includes(s));

    // Fallback response if everything fails
    return {
      message: "I'm having some technical difficulties right now, but I'd love to chat! Feel free to reach out to Joe directly at j.hamilton@servetdigital.com or connect on LinkedIn. Thanks for your interest!",
      context: [],
      sources: [],
      isNetworkError
    };
  }
}

// Fire-and-forget: tell the edge function to classify topics + mark session ended.
export async function endChatSession(): Promise<void> {
  const sessionId = localStorage.getItem('joeai_session_id');
  if (!sessionId) return;
  try {
    await supabase.functions.invoke('ai-chat', {
      body: { sessionId, end_session: true },
    });
  } catch (err) {
    console.warn('endChatSession failed:', err);
  }
}

export function getWelcomeMessage(): AIResponse {
  return {
    message: "Hey! I'm an AI trained on Joe's work, projects, and experience. Ask me anything - what he's built, his technical approach, or how he solves complex problems."
  };
}

// Test connection by calling Supabase directly — NOT through generateAIResponse,
// which catches errors silently and returns a fallback that looks like success.
export async function testAISystem(): Promise<boolean> {
  try {
    console.log('🧪 Testing AI system via edge function...');

    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: {
        message: 'Test message',
        sessionId: 'connection-test'
      }
    });

    if (error) {
      console.error('❌ AI system test failed: edge function error', error);
      return false;
    }

    if (data?.response) {
      console.log('✅ AI system test passed!');
      return true;
    }

    console.error('❌ AI system test failed: no response in data');
    return false;
  } catch (error) {
    console.error('❌ AI system test failed:', error);
    return false;
  }
}

// Test edge function directly
export async function testEdgeFunction(): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: {
        message: "Hello, this is a test",
        sessionId: "test-session"
      }
    });
    
    if (error) {
      throw error;
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('Edge function test failed:', error);
    return { success: false, error: error.message };
  }
}